import { Autocomplete, Checkbox, CircularProgress, InputLabel, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { searchScopeTemplates } from '../../../../services/scopetemplate.service';
import { ScopeTemplate } from '../../../../types/ScopeTemplate';
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import { assignScopeTemplateToUser, fetchScopeTemplatesAssignedToUser, unassignScopeTemplateFromUser } from '../../../../redux/slices/userScopeSlice';

interface UserScopeTemplateViewProps {
  userId: string;
}

let timeout: ReturnType<typeof setTimeout>;

const UserScopeTemplateView = (props: UserScopeTemplateViewProps) => {
  const dispatch = useAppDispatch();
  const userScopeState = useAppSelector((state) => state.userScopes);
  const { assignedTemplates, isAssignedTemplatesLoading } = userScopeState;

  const [options, setOptions] = useState<readonly ScopeTemplate[]>([]);
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchScopeTemplatesAssignedToUser(props.userId));
  }, [props.userId]);

  const onValueChange = async (event: any, newValue: ScopeTemplate | null) => {
    if (newValue) {
      dispatch(assignScopeTemplateToUser({ userId: props.userId, templateId: newValue.id }));

      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }
  };

  const onInputChange = (event: any, value: string) => {
    setTimerForAutocomplete(value);
  };

  const onCheckBoxChange = async (item: ScopeTemplate) => {
    dispatch(unassignScopeTemplateFromUser({ userId: props.userId, templateId: item.id }));
  };

  const getSearchOptions = async (input: string) => {
    if (input.length < 1) {
      return;
    }

    await searchScopeTemplates(input).then((response) => {
      if (!response?.data) {
        console.error('No results', response);
        return [];
      }
      setOptions(response.data);
    });
  };

  const setTimerForAutocomplete = (input: string) => {
    timeout && clearTimeout(timeout);
    timeout = setTimeout(() => getSearchOptions(input), 100);
  };

  return (
    <>
      <Autocomplete
        filterOptions={(x) => x} // disable default filtering, because we filter in the backend
        id='serialNumber'
        options={options}
        renderOption={(props, option) => (
          <span {...props} key={option.id}>
            {option.name}
          </span>
        )}
        getOptionLabel={(option) => option.name ?? ''}
        noOptionsText='No options'
        clearOnBlur={true}
        blurOnSelect={true}
        includeInputInList
        renderInput={(params) => (
          <TextField
            variant='standard'
            {...params}
            label={'Search...'}
            inputRef={textFieldRef} // Assign ref to the TextField component
          />
        )}
        onChange={onValueChange}
        onInputChange={onInputChange}
        value={null} // Reset value after selection
      />
      <div style={{ height: '1em', paddingTop: '0.5em', marginBottom: '0.5em', paddingLeft: '0.75em' }}>
        {isAssignedTemplatesLoading && <CircularProgress style={{ height: '1em', width: '1em' }} />}
      </div>
      <div>
        {!Array.isArray(assignedTemplates) || assignedTemplates.length === 0 ? (
          <span className='txt-muted'>No values assigned</span>
        ) : (
          assignedTemplates.map((x, idx) => (
            <InputLabel key={idx} style={{ width: '100%', cursor: 'pointer' }}>
              <Checkbox checked={true} onChange={() => onCheckBoxChange(x)} />
              {x.name}
            </InputLabel>
          ))
        )}
      </div>
    </>
  );
};

export default UserScopeTemplateView;
